import clsx from 'clsx';
import { Button } from '../../typography/button/button';
import { useLocalization } from '@/hooks/use-localization';
import { extractLangFromSpan } from '@/utils/lang-span';

type ClickableInfoboxProps = {
    title: string;
    subtext: string;
    linkTo: {
        url: string;
        title: string;
    };
};

export const ClickableInfobox = ({ title, subtext, linkTo }: ClickableInfoboxProps) => {
    const { localize } = useLocalization();
    const { lang: titleLang, content: titleContent } = extractLangFromSpan(title);
    const { lang: subtextLang, content: subtextContent } = extractLangFromSpan(subtext);
    const hasLink = Boolean(linkTo?.url?.length);

    return (
        <div
            data-component="clickable-infobox"
            className={clsx(
                'r-relative r-flex r-w-full r-flex-col r-py-8 r-transition-all r-duration-200 last-of-type:r-pb-0 md:r-px-6 md:r-py-16 md:r-shadow-xl',
                {
                    'focus-within:md:r-[box-shadow:0_0_0_2px_white,0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] md:r-cursor-pointer md:r-outline md:r-outline-2 md:r-outline-offset-2 md:r-outline-transparent focus-within:md:r-outline-focus-blue md:hover:r-bg-gray-100':
                        hasLink,
                }
            )}
        >
            <h2 className="r-mb-6 r-text-2xl r-font-medium" lang={titleLang ?? undefined}>
                <span dangerouslySetInnerHTML={{ __html: titleContent }} />
                {hasLink && (
                    <a
                        href={linkTo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-focus-indicator no-icon r-hidden after:r-absolute after:r-inset-0 after:!r-top-0 after:r-z-10 md:r-inline"
                    >
                        <span className="r-sr-only">
                            <span dangerouslySetInnerHTML={{ __html: titleContent }} /> ({localize('external-link')}, {localize('opens-in-new-tab')})
                        </span>
                        <img
                            src="/assets/icons/arrow-out-black.svg"
                            alt=""
                            aria-hidden="true"
                            className="r-relative r-ml-2 r-inline-block r-size-4"
                        />
                    </a>
                )}
            </h2>

            {subtextContent.length ? <p lang={subtextLang ?? undefined} dangerouslySetInnerHTML={{ __html: subtextContent }} /> : null}
            {hasLink && (
                <Button to={linkTo.url} target="_blank" className="r-mt-4 md:r-hidden" size="small" fullWidth>
                    {localize('read-more')}
                    <span className="r-sr-only">
                        {' '}
                        <span dangerouslySetInnerHTML={{ __html: titleContent }} /> ({localize('external-link')}, {localize('opens-in-new-tab')})
                    </span>
                </Button>
            )}
        </div>
    );
};
